/**
 * Safe rendering for ConfirmModal messages (review finding H7).
 *
 * THE PROBLEM
 * -----------
 * `ConfirmModal` rendered its `message` with `dangerouslySetInnerHTML`, and a
 * comment claimed "the message text is always authored by the app, not user
 * input". That was not true: of the 112 call sites, 94 interpolate a value
 * fetched from the server, and several of those values are free text a
 * low-privileged person controls — a file or folder name on a remote device,
 * a service / startup-item / installed-app name, a registry value name, a
 * device name. Naming a file `<img src=x onerror=...>` and getting an
 * operator to browse that folder was script execution inside their
 * authenticated Omni session.
 *
 * THE FIX
 * -------
 * The messages genuinely do carry app-authored markup — 256 `<strong>`, 88
 * `<br>`, and a handful of `<small>`/`<div>`/`<i>`/`<code>`/`<em>` with
 * `style`/`class` for the callout boxes — so "escape everything" would have
 * broken every call site. Instead the message is parsed, filtered against an
 * allowlist derived from what the call sites actually use, and turned into
 * **React nodes**.
 *
 * That is deliberately stronger than sanitising to an HTML string: there is
 * no `innerHTML` sink left at all, React escapes every text node itself, and
 * the only elements that can ever be created are the ones named in
 * ALLOWED_TAGS below. A bug in the filter can therefore produce wrong
 * *formatting*, but it cannot produce script execution.
 *
 * This module is the pure half — it returns a plain description of the tree so
 * it can be tested without React. `ConfirmMessage.jsx` maps it to elements.
 */

/**
 * Tags a message may contain. Derived by enumerating every existing call
 * site, plus the obvious list siblings (`p`/`ul`/`ol`/`li`/`span`) so a new
 * message can use them without needing this file changed.
 */
export const ALLOWED_TAGS = new Set([
  'strong',
  'b',
  'em',
  'i',
  'small',
  'br',
  'code',
  'div',
  'span',
  'p',
  'ul',
  'ol',
  'li'
]);

/**
 * Tags whose *content* is code or markup rather than prose. These are dropped
 * whole. Everything else that is not allowed is "unwrapped" — the element goes
 * but its text stays — because losing an operator-visible name silently is its
 * own hazard: a file called `<b>report.pdf` should still read as
 * "report.pdf", not vanish.
 */
const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'template',
  'noscript',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'svg',
  'math',
  'head',
  'title',
  'base',
  'link',
  'meta',
  'audio',
  'video',
  'source',
  'track',
  'canvas',
  'portal',
  'xmp'
]);

/**
 * CSS properties a message may set. Every one of these is in use by an
 * existing callout box. Values containing `(` are refused outright, which
 * removes `url(...)` (a beacon, and the only real risk in an inline style)
 * along with every other function.
 */
const ALLOWED_STYLE_PROPS = new Set([
  'color',
  'background',
  'background-color',
  'padding',
  'padding-top',
  'padding-bottom',
  'padding-left',
  'padding-right',
  'margin',
  'margin-top',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'border',
  'border-left',
  'border-right',
  'border-top',
  'border-bottom',
  'border-radius',
  'font-size',
  'font-weight',
  'font-style',
  'line-height',
  'text-align',
  'text-decoration',
  'word-break',
  'white-space',
  'display'
]);

/** Font Awesome and utility class names only — no punctuation, no escapes. */
const CLASS_PATTERN = /^[A-Za-z0-9 _-]{1,120}$/;

/** Guards against a pathological or hostile message. */
const MAX_DEPTH = 12;
const HARD_DEPTH = 200;
const MAX_NODES = 400;

/**
 * Convert a CSS declaration string into a React style object, keeping only
 * allowlisted properties.
 *
 * @param {string} raw
 * @returns {Record<string, string> | null}
 */
export function sanitizeStyle(raw) {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > 500) return null;
  /** @type {Record<string, string>} */
  const out = {};
  let kept = 0;
  for (const declaration of raw.split(';')) {
    const idx = declaration.indexOf(':');
    if (idx <= 0) continue;
    const prop = declaration.slice(0, idx).trim().toLowerCase();
    const value = declaration.slice(idx + 1).trim();
    if (!ALLOWED_STYLE_PROPS.has(prop)) continue;
    if (value.length === 0 || value.length > 120) continue;
    // No functions at all: kills url(), and there is no legitimate use of one
    // in a confirmation message.
    if (/[('"\\]/.test(value)) continue;
    if (/[<>]/.test(value)) continue;
    // camelCase for React: border-left -> borderLeft
    const key = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[key] = value;
    kept += 1;
    if (kept >= 20) break;
  }
  return kept > 0 ? out : null;
}

/**
 * @param {string} raw
 * @returns {string | null}
 */
export function sanitizeClass(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed || !CLASS_PATTERN.test(trimmed)) return null;
  return trimmed;
}

/**
 * Fallback for an environment with no DOM parser (never the browser, but
 * keeps this importable from a plain Node context). Fails closed: strips all
 * markup and returns prose, so the worst case is an unformatted message.
 *
 * @param {string} html
 * @returns {string}
 */
export function stripMarkup(html) {
  return String(html ?? '')
    .replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&');
}

/**
 * @typedef {string | { tag: string, style?: Record<string,string>, className?: string, children: ConfirmNode[] }} ConfirmNode
 */

/**
 * Parse a confirm message and return a filtered tree description.
 *
 * @param {unknown} html
 * @param {{ parser?: { parseFromString: (s: string, t: string) => Document } }} [options]
 * @returns {ConfirmNode[]}
 */
export function sanitizeConfirmMessage(html, options = {}) {
  if (html === null || html === undefined) return [];
  const source = typeof html === 'string' ? html : String(html);
  if (source.length === 0) return [];

  const Parser = options.parser ?? (typeof DOMParser !== 'undefined' ? new DOMParser() : null);
  if (!Parser) return [stripMarkup(source)];

  /** @type {Document} */
  let doc;
  try {
    // A DOMParser document has no browsing context: scripts do not run and
    // subresources are not fetched while we inspect it. Nothing from it is
    // ever inserted into the live page — only the description below is.
    doc = Parser.parseFromString(source, 'text/html');
  } catch {
    return [stripMarkup(source)];
  }
  if (!doc || !doc.body) return [stripMarkup(source)];

  let budget = MAX_NODES;

  /**
   * @param {Node} node
   * @param {number} depth
   * @returns {ConfirmNode[]}
   */
  function convert(node, depth) {
    if (budget <= 0) return [];
    // Absolute recursion stop. The HTML parser caps open elements long before
    // this, but a message is not worth a stack overflow.
    if (depth > HARD_DEPTH) return [];

    // Text
    if (node.nodeType === 3) {
      budget -= 1;
      const text = node.nodeValue ?? '';
      return text.length > 0 ? [text] : [];
    }

    // Anything that is not an element (comment, CDATA, doctype) contributes
    // nothing an operator needs to read.
    if (node.nodeType !== 1) return [];

    const element = /** @type {Element} */ (node);
    const tag = String(element.tagName ?? '').toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) return [];

    const childNodes = Array.from(element.childNodes ?? []);
    const children = childNodes.flatMap((child) => convert(child, depth + 1));

    // Past the nesting ceiling, keep filtering but stop building elements —
    // the prose comes through unwrapped. Returning [] here would have
    // silently swallowed the body of a deeply nested message, which is the
    // same "the operator cannot see the real value" problem in another guise.
    if (depth >= MAX_DEPTH) return children;

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep the prose, lose the element.
      return children;
    }

    budget -= 1;
    /** @type {{ tag: string, style?: Record<string,string>, className?: string, children: ConfirmNode[] }} */
    const out = { tag, children };

    const styleAttr = element.getAttribute ? element.getAttribute('style') : null;
    const style = styleAttr ? sanitizeStyle(styleAttr) : null;
    if (style) out.style = style;

    const classAttr = element.getAttribute ? element.getAttribute('class') : null;
    const className = classAttr ? sanitizeClass(classAttr) : null;
    if (className) out.className = className;

    return [out];
  }

  return Array.from(doc.body.childNodes).flatMap((child) => convert(child, 0));
}
